import { Box, Paper, Typography } from "@mui/material"
import DashboardRoundedIcon from "@mui/icons-material/DashboardRounded"
import InsightsRoundedIcon from "@mui/icons-material/InsightsRounded"
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded"
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded"
import PrecisionManufacturingRoundedIcon from "@mui/icons-material/PrecisionManufacturingRounded"
import LocalShippingRoundedIcon from "@mui/icons-material/LocalShippingRounded"
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded"
import { Section } from "../section/section"

const FEATURES = [
  {
    icon: DashboardRoundedIcon,
    color: "#60a5fa",
    title: "Business Overview Dashboard",
    points: [
      "Complete overview",
      "KPI tracking",
      "Performance monitoring",
      "Operational insights",
    ],
  },
  {
    icon: InsightsRoundedIcon,
    color: "#34d399",
    title: "Analytics",
    points: [
      "Revenue analytics",
      "Product insights",
      "Manufacturing trends",
      "Growth tracking",
    ],
  },
  {
    icon: GroupsRoundedIcon,
    color: "#a78bfa",
    title: "Manager Control",
    points: [
      "Add/remove managers",
      "Directory view",
      "Role-based access",
      "Permission control",
    ],
  },
  {
    icon: Inventory2RoundedIcon,
    color: "#fbbf24",
    title: "Product Management",
    points: [
      "Inventory tracking",
      "Product lifecycle",
      "Stock updates",
      "Catalog control",
    ],
  },
  {
    icon: PrecisionManufacturingRoundedIcon,
    color: "#fb7185",
    title: "Manufacturing Workflow",
    points: [
      "Production planning",
      "Quantity allocation",
      "Status tracking",
      "Workflow control",
    ],
  },
  {
    icon: LocalShippingRoundedIcon,
    color: "#22d3ee",
    title: "Delivery Management",
    points: [
      "Dispatch tracking",
      "Release control",
      "Fulfillment monitoring",
      "Logistics overview",
    ],
  },
  {
    icon: ReceiptLongRoundedIcon,
    color: "#4ade80",
    title: "Revenue Logs",
    points: [
      "Revenue tracking",
      "Financial logs",
      "Date-wise records",
      "Categorization",
    ],
  },
]

export function Features() {
  return (
    <Section
      id="features"
      sx={{
        position: "relative",
        overflow: "hidden",
        py: { xs: 10, md: 16 },

        background: `
          radial-gradient(circle at top left, rgba(59,130,246,0.15), transparent 30%),
          radial-gradient(circle at bottom right, rgba(14,165,233,0.12), transparent 35%),
          linear-gradient(180deg, #020617 0%, #0f172a 45%, #111827 100%)
        `,
      }}
    >
      {/* Background Glow */}
      <Box
        sx={{
          position: "absolute",
          top: -150,
          left: -150,
          width: 400,
          height: 400,
          borderRadius: "50%",
          background: "#2563eb",
          filter: "blur(140px)",
          opacity: 0.12,
        }}
      />

      <Box
        sx={{
          position: "absolute",
          bottom: -180,
          right: -180,
          width: 450,
          height: 450,
          borderRadius: "50%",
          background: "#06b6d4",
          filter: "blur(160px)",
          opacity: 0.1,
        }}
      />

      {/* Header */}
      <Box
        sx={{
          textAlign: "center",
          maxWidth: 760,
          mx: "auto",
          mb: 8,
          position: "relative",
          zIndex: 2,
        }}
      >
        <Typography
          sx={{
            color: "#60a5fa",
            fontWeight: 700,
            fontSize: "0.9rem",
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            mb: 1.5,
          }}
        >
          Features
        </Typography>

        <Typography
          sx={{
            fontSize: { xs: "2.2rem", md: "3.5rem" },
            fontWeight: 800,
            color: "#f8fafc",
            lineHeight: 1.1,
            mb: 2,
          }}
        >
          Everything you need to run operations
        </Typography>

        <Typography
          sx={{
            color: "rgba(248,250,252,0.7)",
            fontSize: { xs: "1rem", md: "1.1rem" },
            lineHeight: 1.7,
          }}
        >
          From product inventory to manufacturing and revenue,
          FlowSync brings every workflow into one connected platform.
        </Typography>
      </Box>

      {/* Features Grid */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2,1fr)",
            lg: "repeat(4,1fr)",
          },
          gap: 4,
          position: "relative",
          zIndex: 2,
        }}
      >
        {FEATURES.map((feature) => {
          const Icon = feature.icon

          return (
            <Paper
              key={feature.title}
              elevation={0}
              sx={{
                position: "relative",
                overflow: "hidden",

                p: 3.5,
                borderRadius: "24px",

                background:
                  "linear-gradient(180deg, #111827 0%, #0f172a 100%)",

                border: "1px solid rgba(59,130,246,0.12)",

                boxShadow: `
                  0 10px 30px rgba(0,0,0,0.35),
                  inset 0 1px 0 rgba(255,255,255,0.03)
                `,

                transition: "all .35s ease",

                "&::before": {
                  content: '""',
                  position: "absolute",
                  inset: 0,
                  borderRadius: "inherit",
                  background: `
                    linear-gradient(
                      135deg,
                      rgba(59,130,246,.12),
                      transparent 40%,
                      transparent 60%,
                      rgba(59,130,246,.08)
                    )
                  `,
                  pointerEvents: "none",
                },

                "&::after": {
                  content: '""',
                  position: "absolute",
                  width: 120,
                  height: 120,
                  top: -40,
                  right: -40,
                  borderRadius: "50%",
                  background: feature.color,
                  opacity: 0.08,
                  filter: "blur(50px)",
                },

                "&:hover": {
                  transform: "translateY(-10px)",
                  borderColor: feature.color,

                  boxShadow: `
                    0 20px 40px rgba(0,0,0,0.45),
                    0 0 30px ${feature.color}20
                  `,
                },
              }}
            >
              {/* Icon */}
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: "18px",
                  display: "grid",
                  placeItems: "center",
                  mb: 2.5,

                  background: `linear-gradient(
                    135deg,
                    ${feature.color},
                    ${feature.color}99
                  )`,

                  boxShadow: `0 10px 25px ${feature.color}40`,
                }}
              >
                <Icon
                  sx={{
                    color: "#fff",
                    fontSize: 30,
                  }}
                />
              </Box>

              {/* Title */}
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: "1.15rem",
                  color: "#f8fafc",
                  mb: 2,
                }}
              >
                {feature.title}
              </Typography>

              {/* Points */}
              <Box component="ul" sx={{ m: 0, p: 0, listStyle: "none" }}>
                {feature.points.map((point) => (
                  <Box
                    component="li"
                    key={point}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.2,
                      mb: 1.2,
                    }}
                  >
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        bgcolor: feature.color,
                        boxShadow: `0 0 12px ${feature.color}`,
                        flexShrink: 0,
                      }}
                    />

                    <Typography
                      sx={{
                        color: "rgba(248,250,252,0.72)",
                        fontSize: "0.95rem",
                      }}
                    >
                      {point}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Paper>
          )
        })}
      </Box>
    </Section>
  )
}

